import type { CloseRequestLayer } from '../types/close-request.type';

/**
 * Select the foreground modal's newest leaf, or the newest non-modal leaf.
 * Modal ownership shields independent drawers even if they open later. Within
 * that scope, React ancestry keeps portal children ahead of parent effects.
 * @param layers - Open layers in registration order.
 * @returns The foreground leaf, or undefined when no layers are open.
 */
export function getTopmostCloseLayer(layers: CloseRequestLayer[]) {
  const byId = new Map(layers.map((layer) => [layer.id, layer]));
  const ancestry = new Map(
    layers.map((layer) => {
      const ancestors = new Set<symbol>();
      let parentId = layer.parentId;
      while (parentId !== null && !ancestors.has(parentId)) {
        ancestors.add(parentId);
        parentId = byId.get(parentId)?.parentId ?? null;
      }
      return [layer.id, ancestors];
    })
  );
  const isAncestor = (parent: CloseRequestLayer, child: CloseRequestLayer) =>
    ancestry.get(child.id)?.has(parent.id) ?? false;
  const modals = layers.filter((layer) => layer.modal);
  const foreground = modals
    .filter((modal) => !modals.some((other) => isAncestor(modal, other)))
    .at(-1);
  const eligible = foreground
    ? layers.filter(
        (layer) => layer === foreground || isAncestor(foreground, layer)
      )
    : layers;
  return eligible
    .filter((layer) => !eligible.some((other) => isAncestor(layer, other)))
    .at(-1);
}
